// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../api/client';
import { getActiveTenant } from '../../../helpers/tenantHelpers';
import { FormRow, SaveBar, Section, SectionRail } from './primitives';

/* Workspace — your business identity, brand, what's switched on, and
   the rest of your tools. Single 720px form column, one save bar. */

const SECTIONS = [
  { id: 'company', label: 'Company' },
  { id: 'branding', label: 'Branding' },
  { id: 'features', label: "What's on" },
  { id: 'tools', label: 'More tools' },
];

export default function WorkspaceTab() {
  const navigate = useNavigate();
  const tenant = getActiveTenant() || {};
  const [prefs, setPrefs] = useState(null);
  const [emailCfg, setEmailCfg] = useState(null);
  const [form, setForm] = useState({ from_display_name: '' });
  const [baseline, setBaseline] = useState({ from_display_name: '' });
  const [saving, setSaving] = useState(false);

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(baseline), [form, baseline]);

  useEffect(() => {
    api.get('/accounts/preferences/').then((r) => setPrefs(r.data?.preferences || {})).catch(() => {});
    api.get('/accounts/email-settings/').then((r) => {
      setEmailCfg(r.data);
      const seed = { from_display_name: r.data?.from_display_name || '' };
      setForm(seed); setBaseline(seed);
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch('/accounts/email-settings/', form);
      setEmailCfg(data);
      setBaseline({ ...form });
      toast.success('Saved');
    } catch {
      toast.error('Could not save');
    } finally {
      setSaving(false);
    }
  };

  const features = prefs?.features || {};
  const domainVerified = emailCfg?.domain_status === 'verified';

  /* FRAME RULE: nothing in Settings navigates out of Settings. */
  const TOOLS = [
    // Services & Pricing lives under Scheduling (next to Service types) — one place, not two.
    { label: 'Integrations', sub: 'Connected apps and services', go: '/settings/integrations' },
    { label: 'Documents', sub: 'Contracts, waivers and uploads', go: '/settings/documents' },
    { label: 'AI assistant', sub: 'Personality and automation level', go: '/settings/ai' },
    { label: 'Automations', sub: 'Follow-up sequences and triggers', go: '/settings/automations' },
  ];

  return (
    <div className="sv2-body">
      <SectionRail sections={SECTIONS} />
      <div className="sv2-col">
        <Section id="company" title="Company"
          sub="Your business identity across Abon and everything you send.">
          <FormRow label="Business name" value={tenant.name} />
          <FormRow label="Website domain"
            help="Verified for email sending — customers see this domain.">
            <div className="sv2-value mono" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
              {tenant.domain || '—'}
              <span className={`sv2-pill ${domainVerified ? 'ok' : 'warn'}`}>
                {domainVerified ? 'Verified' : 'Pending'}
              </span>
            </div>
          </FormRow>
          <FormRow label="Display name"
            help="The sender name on every email your business sends.">
            <input type="text" value={form.from_display_name} placeholder={tenant.name || 'Business name'}
              onChange={(e) => setForm((p) => ({ ...p, from_display_name: e.target.value }))} />
          </FormRow>
        </Section>

        <Section id="branding" title="Branding"
          sub="How Abon looks for your team, and what customers see.">
          <FormRow label="Accent color"
            help="Pick from the palette in the top-bar theme menu — it applies everywhere.">
            <div className="sv2-value" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
              <span aria-hidden style={{ width: 14, height: 14, borderRadius: 4, background: 'var(--accent)' }} />
              Current accent
            </div>
          </FormRow>
          <FormRow label="Customer portal look"
            help="Logo, background photo and tagline for the customer portal.">
            <div className="sv2-value">
              <button className="sv2-link" onClick={() => navigate('/settings/portal')}>
                Edit in the Portal tab →
              </button>
            </div>
          </FormRow>
        </Section>

        <Section id="features" title="What's on"
          sub="Where your workspace's smart features stand. These switch on with your go-ahead — not from this page.">
          <div>
            {[
              ['AI follow-ups', features.ai_followup_enabled, 'Drafts follow-up messages for leads that go quiet.'],
              ['AI scheduling', features.ai_scheduling_enabled, 'Suggests appointment times automatically.'],
              ['Contact enrichment', features.enrichment_enabled, 'Fills in extra detail on new leads.'],
              ['Instant quotes', features.auto_quote_enabled, 'Builds a draft quote from a request.'],
            ].map(([label, on, copy]) => (
              <div className="sv2-status-row" key={label}>
                <span className="sv2-label">{label}</span>
                <span className={`sv2-pill ${on ? 'ok' : 'off'}`}>{on ? 'On' : 'Off'}</span>
                <span className="sv2-status-copy">{copy}</span>
              </div>
            ))}
          </div>
        </Section>

        <Section id="tools" title="More tools"
          sub="The rest of your workspace setup.">
          <div className="sv2-obj-list">
            {TOOLS.map((t) => (
              <div className="sv2-obj" key={t.label}>
                <div className="sv2-obj-head">
                  <div style={{ minWidth: 0 }}>
                    <div className="sv2-obj-title">{t.label}</div>
                    <div className="sv2-obj-sub">{t.sub}</div>
                  </div>
                  <button className="sv2-btn sm" onClick={() => navigate(t.go)}>Open</button>
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <SaveBar dirty={dirty} saving={saving} onSave={save}
        onDiscard={() => setForm(baseline)} />
    </div>
  );
}
